import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Users, BarChart3, Shield, Sparkles, ArrowRight, CheckCircle2,
  Target, TrendingUp, FileText, Brain, Heart, ChevronRight,
  Star, Zap, Globe, Lock
} from 'lucide-react';

const TEAL = '#00A3A1';
const PINK = '#E85B8F';

// ─── Floating Dashboard Mockup ───
function DashboardMockup() {
  return (
    <div className="relative w-full max-w-[540px]" data-testid="hero-mockup">
      {/* Main dashboard card */}
      <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,163,161,0.12)] border border-slate-100 overflow-hidden">
        {/* Fake sidebar + content */}
        <div className="flex">
          <div className="w-12 bg-slate-50 border-r border-slate-100 p-2 space-y-3 hidden sm:block">
            <div className="w-8 h-8 rounded-lg bg-teal-100" />
            <div className="w-8 h-2 rounded bg-slate-200" />
            <div className="w-8 h-2 rounded bg-slate-200" />
            <div className="w-8 h-2 rounded bg-teal-200" />
            <div className="w-8 h-2 rounded bg-slate-200" />
          </div>
          <div className="flex-1 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10px] text-slate-400" style={{ fontFamily: 'Manrope' }}>Good morning</p>
                <p className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>Sarah Johnson</p>
              </div>
              <div className="flex gap-1.5">
                <div className="h-6 px-2 rounded-md bg-teal-50 text-[9px] font-semibold flex items-center" style={{ color: TEAL }}>+ Add Client</div>
              </div>
            </div>
            {/* KPI row */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { label: 'CLIENTS', val: '12', sub: 'Active' },
                { label: 'REVENUE', val: '$48K', sub: 'Monthly' },
                { label: 'HEALTH', val: '94%', sub: 'Average', color: TEAL },
              ].map(k => (
                <div key={k.label} className="bg-slate-50 rounded-lg p-2 text-center">
                  <p className="text-[8px] uppercase tracking-wider text-slate-400 font-medium">{k.label}</p>
                  <p className="text-base font-bold" style={{ color: k.color || '#0F172A', fontFamily: 'Outfit' }}>{k.val}</p>
                  <p className="text-[8px] text-slate-400">{k.sub}</p>
                </div>
              ))}
            </div>
            {/* Client list */}
            <div className="space-y-1.5">
              {[
                { name: 'Acme Corp', health: 92, status: 'Active' },
                { name: 'Bright Studios', health: 87, status: 'Active' },
                { name: 'Coral Finance', health: 65, status: 'At Risk' },
              ].map(c => (
                <div key={c.name} className="flex items-center gap-2 bg-white border border-slate-100 rounded-lg px-2.5 py-1.5">
                  <div className="w-6 h-6 rounded-full text-[8px] font-bold text-white flex items-center justify-center"
                    style={{ background: c.health > 80 ? TEAL : PINK }}>{c.name[0]}</div>
                  <div className="flex-1">
                    <p className="text-[10px] font-semibold text-slate-800">{c.name}</p>
                    <p className="text-[8px] text-slate-400">{c.status}</p>
                  </div>
                  <div className="text-[9px] font-bold" style={{ color: c.health > 80 ? TEAL : PINK }}>{c.health}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating stat cards */}
      <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg border border-slate-100 px-3 py-2 z-10 animate-[float_3s_ease-in-out_infinite]">
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4" style={{ color: TEAL }} />
          <div>
            <p className="text-[9px] text-slate-400">Health Score</p>
            <p className="text-sm font-bold" style={{ color: TEAL }}>94%</p>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-3 -left-3 bg-white rounded-xl shadow-lg border border-slate-100 px-3 py-2 z-10 animate-[float_3s_ease-in-out_infinite_0.5s]">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4" style={{ color: PINK }} />
          <div>
            <p className="text-[9px] text-slate-400">Revenue</p>
            <p className="text-sm font-bold text-slate-800">+23%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Feature Bento Card ───
function BentoCard({ icon: Icon, title, description, color, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-100 p-6 md:p-8 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,163,161,0.08)] hover:border-teal-100 transition-all duration-300 ${className}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${color}12` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{title}</h3>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">{description}</p>
      {children}
    </div>
  );
}

// ─── App Showcase Tab ───
function ShowcaseView({ view }) {
  if (view === 'dashboard') {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-sm font-bold" style={{ color: TEAL }}>SJ</div>
          <div><p className="text-sm font-bold text-slate-800">Good morning, Sarah</p><p className="text-xs text-slate-400">Johnson & Partners · Monday 27 April</p></div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[{ l: 'Active Clients', v: '12' }, { l: 'Overdue', v: '1', c: PINK }, { l: 'This Week', v: '8' }, { l: 'Proposals', v: '3' }].map(k => (
            <div key={k.l} className="bg-slate-50 rounded-lg p-2.5 text-center">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">{k.l}</p>
              <p className="text-xl font-bold mt-0.5" style={{ color: k.c || '#0F172A', fontFamily: 'Outfit' }}>{k.v}</p>
            </div>
          ))}
        </div>
        <div className="bg-teal-50/50 rounded-lg p-3 border border-teal-100">
          <p className="text-[10px] font-semibold text-teal-700 mb-1">Action Required</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs"><span className="text-slate-700">Review Q2 financials</span><span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-500 font-semibold">OVERDUE</span></div>
            <div className="flex items-center justify-between text-xs"><span className="text-slate-700">Prepare tax summary</span><span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">DUE SOON</span></div>
          </div>
        </div>
      </div>
    );
  }
  if (view === 'client') {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl text-sm font-bold text-white flex items-center justify-center" style={{ background: TEAL }}>A</div>
          <div className="flex-1"><p className="text-sm font-bold text-slate-800">Acme Corp</p><p className="text-xs text-slate-400">Jane Doe · jane@acme.com</p></div>
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-semibold">Active</span>
        </div>
        <div className="grid grid-cols-4 gap-2 text-center">
          {[{ l: 'Goals', v: '6', s: '4 done' }, { l: 'Tasks', v: '14', s: '9 done' }, { l: 'Overdue', v: '0', s: 'all clear', c: '#10B981' }, { l: 'Health', v: '92%', c: TEAL }].map(k => (
            <div key={k.l} className="bg-slate-50 rounded-lg p-2">
              <p className="text-[8px] uppercase tracking-wider text-slate-400 font-medium">{k.l}</p>
              <p className="text-base font-bold" style={{ color: k.c || '#0F172A', fontFamily: 'Outfit' }}>{k.v}</p>
              {k.s && <p className="text-[8px] text-slate-400">{k.s}</p>}
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
            <span className="text-slate-700">Q3 Revenue Target</span>
            <div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-slate-200 rounded-full"><div className="h-1.5 rounded-full" style={{ width: '72%', background: TEAL }} /></div><span className="text-[10px] font-bold" style={{ color: TEAL }}>72%</span></div>
          </div>
          <div className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
            <span className="text-slate-700">Reduce Operating Costs</span>
            <div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-slate-200 rounded-full"><div className="h-1.5 rounded-full" style={{ width: '45%', background: PINK }} /></div><span className="text-[10px] font-bold" style={{ color: PINK }}>45%</span></div>
          </div>
        </div>
      </div>
    );
  }
  // Financials
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-bold text-slate-800">Profit & Loss</p>
        <div className="flex gap-1">{['Jul', 'Aug', 'Sep', 'Oct'].map(m => <span key={m} className="text-[9px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">{m}</span>)}</div>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="border-b border-slate-200"><th className="text-left py-1 text-[10px] text-slate-400 font-medium">Account</th><th className="text-right py-1 text-[10px] text-slate-400 font-medium">Jul</th><th className="text-right py-1 text-[10px] text-slate-400 font-medium">Aug</th><th className="text-right py-1 text-[10px] text-slate-400 font-medium">Sep</th></tr></thead>
        <tbody>
          {[
            { l: 'Consulting Revenue', v: ['$82,000', '$85,000', '$79,000'], bold: false },
            { l: 'Advisory Fees', v: ['$14,000', '$15,500', '$13,000'], bold: false },
            { l: 'Gross Profit', v: ['$65,000', '$68,500', '$63,500'], bold: true },
            { l: 'Marketing', v: ['$4,200', '$5,000', '$4,800'], bold: false },
            { l: 'Rent', v: ['$3,200', '$3,200', '$3,200'], bold: false },
            { l: 'EBITDA', v: ['$48,600', '$51,300', '$46,500'], bold: true },
          ].map(r => (
            <tr key={r.l} className={r.bold ? 'border-t border-slate-200' : ''}>
              <td className={`py-1 ${r.bold ? 'font-semibold text-slate-800' : 'text-slate-600 pl-2'}`}>{r.l}</td>
              {r.v.map((v, i) => <td key={i} className={`py-1 text-right font-mono ${r.bold ? 'font-semibold text-slate-800' : 'text-slate-500'}`}>{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════
// MAIN LANDING PAGE
// ═══════════════════════════════════════
export default function LandingPage() {
  const navigate = useNavigate();
  const [showcaseTab, setShowcaseTab] = useState('dashboard');

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="landing-page">
      {/* ═══ FLOATING HEADER ═══ */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-100" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <img src="/simplly_logo.png" alt="Simplly" className="h-8 object-contain" data-testid="logo" />
            <nav className="hidden md:flex items-center gap-6">
              {['Features', 'Product', 'Security'].map(l => (
                <a key={l} href={`#${l.toLowerCase()}`} className="text-sm text-slate-500 hover:text-slate-800 transition-colors">{l}</a>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-sm text-slate-600" onClick={() => navigate('/login')} data-testid="login-btn">
              Log In
            </Button>
            <Button className="text-sm text-white px-5" style={{ background: TEAL }} onClick={() => navigate('/login')} data-testid="get-started-btn">
              Get Started <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden pt-16 pb-24 sm:pt-24 sm:pb-32" data-testid="hero-section">
        {/* Background blobs */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${TEAL}30 0%, transparent 70%)` }} />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full opacity-15" style={{ background: `radial-gradient(circle, ${PINK}25 0%, transparent 70%)` }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100 mb-6">
                <Sparkles className="w-3.5 h-3.5" style={{ color: TEAL }} />
                <span className="text-xs font-semibold" style={{ color: TEAL }}>AI-Powered Consultancy Platform</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Outfit' }}>
                Manage clients.<br />
                <span style={{ color: TEAL }}>Grow revenue.</span><br />
                Stay in control.
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-lg">
                Simplly gives consultancies a single platform to manage clients, track financials, 
                and deliver exceptional service — with AI that does the heavy lifting.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Button className="text-base text-white px-7 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all" style={{ background: TEAL }}
                  onClick={() => navigate('/login')} data-testid="hero-cta">
                  Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" className="text-base px-7 py-6 rounded-xl border-slate-200" data-testid="hero-demo">
                  Book a Demo
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-slate-400">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: TEAL }} /> Free 14-day trial</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: TEAL }} /> No credit card</span>
              </div>
            </div>

            {/* Right: Dashboard Mockup */}
            <div className="flex justify-center lg:justify-end">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES BENTO ═══ */}
      <section id="features" className="py-20 sm:py-28" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: TEAL }}>Everything You Need</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Built for consultancies that mean business
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <BentoCard icon={Users} title="Client Management" color={TEAL} className="md:col-span-8"
              description="Organise every client relationship in one place. Health scores, goal tracking, meeting prep — everything you need at a glance.">
              <div className="flex gap-2 flex-wrap">
                {['Acme Corp', 'Bright Studios', 'Coral Finance', 'Delta Group'].map(n => (
                  <div key={n} className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1">
                    <div className="w-4 h-4 rounded-full text-[7px] font-bold text-white flex items-center justify-center" style={{ background: TEAL }}>{n[0]}</div>
                    <span className="text-[10px] text-slate-600 font-medium">{n}</span>
                  </div>
                ))}
              </div>
            </BentoCard>

            <BentoCard icon={Brain} title="AI Meeting Prep" color={PINK} className="md:col-span-4"
              description="One click generates a complete briefing with talking points, action items, and wins to celebrate.">
              <div className="bg-pink-50/50 rounded-lg p-3 border border-pink-100">
                <p className="text-[10px] text-pink-600 font-semibold mb-1">Key Talking Points</p>
                <p className="text-[10px] text-slate-500">Discuss Q3 revenue growth strategy and review outstanding compliance items...</p>
              </div>
            </BentoCard>

            <BentoCard icon={BarChart3} title="Financial Insights" color={TEAL} className="md:col-span-6"
              description="Import Xero & MYOB P&L reports directly. AI reads your PDFs and maps every line item to your dashboard, month by month.">
              <div className="flex gap-3">
                {[{ m: 'Jul', v: 48 }, { m: 'Aug', v: 55 }, { m: 'Sep', v: 42 }, { m: 'Oct', v: 61 }, { m: 'Nov', v: 58 }].map(d => (
                  <div key={d.m} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-md" style={{ height: `${d.v}px`, background: `${TEAL}20` }} />
                    <span className="text-[8px] text-slate-400">{d.m}</span>
                  </div>
                ))}
              </div>
            </BentoCard>

            <BentoCard icon={Users} title="Team Collaboration" color={PINK} className="md:col-span-6"
              description="Invite your team, assign roles, manage seats. Everyone stays aligned with shared client views and real-time activity feeds.">
              <div className="flex items-center -space-x-2">
                {['#00A3A1', '#E85B8F', '#7C3AED', '#F59E0B', '#10B981'].map((c, i) => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-white text-[9px] font-bold text-white flex items-center justify-center" style={{ background: c }}>
                    {['SJ', 'AD', 'MK', 'RL', 'JT'][i]}
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 text-[9px] font-bold text-slate-400 flex items-center justify-center ml-1">+7</div>
              </div>
            </BentoCard>
          </div>
        </div>
      </section>

      {/* ═══ APP SHOWCASE ═══ */}
      <section id="product" className="py-20 sm:py-28 bg-white" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: PINK }}>See It In Action</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Every tool a consultancy needs
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left: Tabs */}
            <div className="lg:col-span-2 space-y-3">
              {[
                { id: 'dashboard', label: 'Command Centre', desc: 'KPIs, action items, and client health at a glance', icon: Target },
                { id: 'client', label: 'Client 360', desc: 'Goals, tasks, documents, and performance in one view', icon: Users },
                { id: 'financials', label: 'P&L Financials', desc: 'Import Xero/MYOB PDFs and see month-by-month breakdowns', icon: BarChart3 },
              ].map(tab => (
                <button key={tab.id} onClick={() => setShowcaseTab(tab.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    showcaseTab === tab.id ? 'bg-teal-50 border-teal-200 shadow-sm' : 'bg-white border-slate-100 hover:border-slate-200'
                  }`} data-testid={`showcase-tab-${tab.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: showcaseTab === tab.id ? `${TEAL}15` : '#F1F5F9' }}>
                      <tab.icon className="w-4 h-4" style={{ color: showcaseTab === tab.id ? TEAL : '#94A3B8' }} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${showcaseTab === tab.id ? 'text-slate-800' : 'text-slate-600'}`}>{tab.label}</p>
                      <p className="text-xs text-slate-400">{tab.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Right: Mockup */}
            <div className="lg:col-span-3" data-testid="showcase-preview">
              <ShowcaseView view={showcaseTab} />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECURITY ═══ */}
      <section id="security" className="py-16 sm:py-20" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${TEAL}12` }}>
              <Lock className="w-8 h-8" style={{ color: TEAL }} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Your data stays yours</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Financial data is sanitised before AI processing — client names, emails, and identifiers are stripped automatically.
                Only account labels and figures are sent. Data is never used for model training.
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              {['PII Stripped', 'Encrypted', 'AU Hosted'].map(b => (
                <span key={b} className="text-[10px] font-semibold px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100" style={{ color: TEAL }}>{b}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-20 sm:py-28" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" style={{ background: TEAL }}>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${PINK} 0%, transparent 70%)` }} />
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
                Ready to simplify your consultancy?
              </h2>
              <p className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
                Join hundreds of consultancies using Simplly to manage clients, track performance, and grow smarter.
              </p>
              <Button className="text-base px-8 py-6 rounded-xl bg-white hover:bg-slate-50 shadow-lg" style={{ color: TEAL }}
                onClick={() => navigate('/login')} data-testid="cta-start">
                Start Your Free Trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-teal-200 text-xs mt-4">No credit card required · 14-day free trial · Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-slate-100 py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <img src="/simplly_logo.png" alt="Simplly" className="h-7 object-contain" data-testid="footer-logo" />
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">The all-in-one platform for modern consultancies.</p>
            </div>
            {[
              { title: 'Product', links: ['Features', 'Security', 'Integrations'] },
              { title: 'Company', links: ['About', 'Careers', 'Contact'] },
              { title: 'Legal', links: ['Privacy', 'Terms', 'DPA'] },
            ].map(col => (
              <div key={col.title}>
                <p className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3">{col.title}</p>
                <div className="space-y-2">
                  {col.links.map(l => <p key={l} className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer transition-colors">{l}</p>)}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-6 flex items-center justify-between">
            <p className="text-xs text-slate-400">© 2026 Simplly. All rights reserved.</p>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span>Made in Australia</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ═══ ANIMATION KEYFRAMES ═══ */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
}
